import { Link } from "react-router-dom";
import { useSeo } from "../lib/seo";
import { PublicHeader, PublicFooter } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { CheckCircle2, ShieldCheck, CreditCard, Building2 } from "lucide-react";

const FEATURES = [
  "Unlimited patient evaluations & saved cases",
  "80+ conditions & medications with referenced guidance",
  "Emergency vs routine pathways for every recommendation",
  "Lab request PDFs, results entry & pending cases",
  "Referral / consultation letter PDFs",
  "Local anaesthetic dose calculator",
  "Email support within 1–3 business days",
];

const PLANS = [
  { id: "monthly", name: "Monthly", price: "$10", per: "/ month", note: "Billed monthly · cancel anytime" },
  { id: "annual", name: "Annual", price: "$100", per: "/ year", note: "Billed yearly · 2 months free", badge: "Best value" },
];

function PlanCard({ p }) {
  return (
    <div data-testid={`plan-${p.id}`} className={`relative rounded-2xl border p-8 bg-white ${p.badge ? "border-[#1C6FE3] shadow-lg shadow-sky-100" : "border-slate-200"}`}>
      {p.badge && <span className="absolute -top-3 left-8 bg-[#1C6FE3] text-white text-xs font-semibold px-3 py-1 rounded-full">{p.badge}</span>}
      <p className="font-heading font-semibold text-[#0B2545]">{p.name}</p>
      <p className="mt-3"><span className="font-heading text-5xl font-bold text-[#0B2545]" data-testid={`plan-${p.id}-price`}>{p.price}</span> <span className="text-slate-500">{p.per}</span></p>
      <p className="text-sm text-slate-500 mt-1">{p.note}</p>
      <Link to="/register"><Button data-testid={`plan-${p.id}-cta`} className="w-full mt-6 h-11 bg-[#0B2545] hover:bg-[#0B2545]/90">Start 7-day free trial</Button></Link>
      <ul className="mt-6 space-y-2.5">
        {FEATURES.map((f) => <li key={f} className="flex gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {f}</li>)}
      </ul>
    </div>
  );
}

export default function Pricing() {
  useSeo({
    title: "Pricing — DentiCision | $10/month, 7-day free trial",
    description: "DentiCision clinical decision support for dentists: 7-day free trial, then USD 10 per month or USD 100 per year. Cancel anytime. Secure payments by Tap (mada, Apple Pay, Visa, Mastercard).",
    path: "/pricing",
    jsonLd: { "@context": "https://schema.org", "@type": "Service", name: "DentiCision subscription", serviceType: "Clinical decision support software subscription for dentists",
      provider: { "@type": "Organization", name: "DentiCision", url: "https://denticision.com" }, areaServed: "Worldwide",
      image: "https://denticision.com/brand/denticision-og-v2.jpg",
      description: "Evidence-based clinical decision support for dentists — 7-day free trial, then monthly or annual subscription.",
      offers: [
        { "@type": "Offer", name: "Monthly", price: "10", priceCurrency: "USD", availability: "https://schema.org/InStock", url: "https://denticision.com/pricing" },
        { "@type": "Offer", name: "Annual", price: "100", priceCurrency: "USD", availability: "https://schema.org/InStock", url: "https://denticision.com/pricing" },
      ] },
  });
  return (
    <div className="min-h-screen bg-slate-50" data-testid="pricing-page">
      <PublicHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#0B2545]">Simple, transparent pricing</h1>
          <p className="text-base md:text-lg text-slate-600 mt-4">Start with a <b>7-day free trial</b> — no card required. Then choose monthly or annual.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-12">{PLANS.map((p) => <PlanCard key={p.id} p={p} />)}</div>
        <div data-testid="plan-institutional" className="mt-6 rounded-2xl bg-[#0B2545] text-white p-8 flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-xl">
            <p className="flex items-center gap-2 text-sm font-semibold text-sky-300"><Building2 className="w-4 h-4" /> For Clinics & Organizations</p>
            <p className="font-heading text-2xl font-bold mt-1">Empower your dental team with evidence-based clinical decision support.</p>
            <p className="text-sm text-sky-100/80 mt-2">Custom quotation for clinics, medical centres, dental groups and universities — centralized dentist licenses, one contract and one renewal date.</p>
          </div>
          <Link to="/institutions"><Button data-testid="plan-institutional-cta" className="h-11 px-6 bg-[#1C6FE3] hover:bg-[#1659b8]">Request a quotation</Button></Link>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-10 text-sm">
          <div className="flex gap-3 rounded-xl bg-white border border-slate-200 p-5">
            <CreditCard className="w-5 h-5 text-[#1C6FE3] shrink-0" />
            <p className="text-slate-600"><b className="text-slate-800">Secure payments by Tap Payments</b> — mada, Apple Pay, Visa and Mastercard. Prices in USD; applicable taxes may apply. Subscriptions renew automatically until cancelled.</p>
          </div>
          <div className="flex gap-3 rounded-xl bg-white border border-slate-200 p-5">
            <ShieldCheck className="w-5 h-5 text-[#1C6FE3] shrink-0" />
            <p className="text-slate-600"><b className="text-slate-800">Cancel anytime</b> — access continues until the end of your paid period. No partial refunds. See the <Link to="/refund-policy" className="text-[#1C6FE3]">Cancellation & Refund Policy</Link> and <Link to="/terms" className="text-[#1C6FE3]">Terms of Service</Link>.</p>
          </div>
        </div>
        <p className="text-xs text-slate-500 text-center mt-8 max-w-2xl mx-auto">DentiCision provides an evidence-based second opinion to assist dentists and does not replace professional clinical judgment or consultation with the patient's physician. <Link to="/medical-disclaimer" className="underline">Medical Disclaimer</Link>.</p>
      </main>
      <PublicFooter />
    </div>
  );
}
